import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { BeneficiaryPerformanceType } from "../../types/beneficiaries";
import { useBeneficiaryPerformanceData } from "@/hooks/useBeneficiaryPerformanceData";

const AccountPerformance: React.FC<{
  beneficiaryData: BeneficiaryPerformanceType;
}> = ({ beneficiaryData }) => {
  const performanceData = useBeneficiaryPerformanceData(beneficiaryData);

  const customTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow">
          <p className="text-sm font-medium">{`Month: ${label}`}</p>
          <p className="text-sm text-teal-600">
            {`Balance: $${Number(payload[0].value).toLocaleString()}`}
          </p>
        </div>
      );
    }
    return null;
  };

  if (performanceData.length === 0) {
    return (
      <div className="flex justify-center items-center p-8">
        <p className="text-[#00A6AD]">No performance data available</p>
      </div>
    );
  }

  return (
    <>
      <div className="w-full h-80 relative mt-6">
        {/* Mobile: 16 columns */}
        <div
          className="absolute inset-0 flex md:hidden"
          style={{ height: "calc(100% - 40px)" }}
        >
          {Array.from({ length: 16 }).map((_, index) => (
            <div
              key={index}
              className={`flex-1 h-full ${
                index % 2 === 0 ? "bg-gray-50" : "bg-transparent"
              }`}
            />
          ))}
        </div>

        {/* Desktop: 32 columns */}
        <div
          className="absolute inset-0 hidden md:flex"
          style={{ height: "calc(100% - 40px)" }}
        >
          {Array.from({ length: 32 }).map((_, index) => (
            <div
              key={index}
              className={`flex-1 h-full ${
                index % 2 === 0 ? "bg-gray-50" : "bg-transparent"
              }`}
            />
          ))}
        </div>

        <ResponsiveContainer>
          <LineChart data={performanceData}>
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f59e0b" />
                <stop offset="50%" stopColor="#eab308" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
            </defs>
            <CartesianGrid
              stroke="#e5e7eb"
              strokeWidth={1}
              horizontal={true}
              vertical={false}
              horizontalPoints={[0]}
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={{ stroke: "#f0f0f0", strokeWidth: 1 }}
              tickLine={false}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });
              }}
            />
            <YAxis
              domain={[0, "dataMax + 100"]}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
              hide={true}
            />
            <Tooltip content={customTooltip} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="url(#gradient)"
              strokeWidth={3}
              dot={{
                r: 5,
                fill: "#14b8a6",
                stroke: "#ffffff",
                strokeWidth: 2,
              }}
              activeDot={{
                r: 6,
                fill: "#14b8a6",
                stroke: "#ffffff",
                strokeWidth: 2,
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

export default AccountPerformance;
